import React, { useRef, useState } from "react";
import { Paperclip, PaperPlaneRight, X, Microphone } from "@phosphor-icons/react";
import { toast } from "sonner";

const ACCEPTED = ["image/jpeg", "image/png", "image/webp"];

export function ChatComposer({ onSend, onVoice, disabled, autoFocus, onFocus, placeholder = "Describe your issue or ask a question..." }) {
  const [text, setText] = useState("");
  const [image, setImage] = useState(null); // {dataUrl, name}
  const fileRef = useRef(null);
  const taRef = useRef(null);

  const pickImage = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!ACCEPTED.includes(f.type)) {
      toast.error("Please upload a JPG, PNG or WEBP image");
      return;
    }
    if (f.size > 8 * 1024 * 1024) {
      toast.error("Image is too large (max 8MB)");
      return;
    }
    const reader = new FileReader();
    reader.onload = () => setImage({ dataUrl: reader.result, name: f.name });
    reader.readAsDataURL(f);
  };

  const submit = () => {
    if (disabled) return;
    if (!text.trim() && !image) return;
    onSend({ message: text.trim() || "(see attached photo)", image: image?.dataUrl || null });
    setText("");
    setImage(null);
    if (taRef.current) taRef.current.style.height = "auto";
  };

  const onKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      submit();
    }
  };

  const autoGrow = (e) => {
    setText(e.target.value);
    e.target.style.height = "auto";
    e.target.style.height = Math.min(e.target.scrollHeight, 180) + "px";
  };

  return (
    <div className="w-full max-w-3xl mx-auto">
      <div className="bg-[#171717] border border-neutral-800 rounded-2xl px-4 py-3 focus-within:border-[#73bf44] transition-colors">
        {image && (
          <div className="mb-3 relative inline-block">
            <img src={image.dataUrl} alt="attachment" className="h-20 rounded-lg border border-neutral-700" />
            <button
              onClick={() => setImage(null)}
              data-testid="composer-remove-image"
              className="absolute -top-2 -right-2 bg-neutral-800 border border-neutral-600 rounded-full p-1 text-neutral-300 hover:text-white"
            >
              <X size={12} />
            </button>
          </div>
        )}
        <textarea
          ref={taRef}
          data-testid="chat-composer-input"
          value={text}
          onChange={autoGrow}
          onKeyDown={onKeyDown}
          onFocus={onFocus}
          rows={1}
          autoFocus={autoFocus}
          enterKeyHint="send"
          autoCapitalize="sentences"
          placeholder={placeholder}
          className="w-full bg-transparent outline-none resize-none text-base sm:text-[0.95rem] text-white placeholder:text-neutral-600 leading-relaxed"
        />
        <div className="flex items-center justify-between mt-2">
          <button
            onClick={() => fileRef.current?.click()}
            data-testid="chat-composer-attach"
            className="text-neutral-500 hover:text-[#73bf44] transition-colors"
            title="Attach a photo"
          >
            <Paperclip size={20} />
          </button>
          <input ref={fileRef} type="file" accept="image/*" onChange={pickImage} className="hidden" />
          <div className="flex items-center gap-2">
          {onVoice && (
            <button
              onClick={onVoice}
              disabled={disabled}
              data-testid="chat-composer-voice"
              title="Talk to Nick (hands-free)"
              className="h-10 pl-3 pr-3.5 rounded-full border border-[#73bf44]/50 text-[#73bf44] flex items-center gap-1.5 text-sm font-medium hover:bg-[#73bf44]/10 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Microphone size={18} weight="fill" /> <span className="hidden sm:inline">Talk</span>
            </button>
          )}
          <button
            onClick={submit}
            disabled={disabled}
            data-testid="chat-composer-send"
            className="bg-[#73bf44] text-[#050505] rounded-full h-10 w-10 flex items-center justify-center hover:bg-[#5fa338] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <PaperPlaneRight size={18} weight="fill" />
          </button>
          </div>
        </div>
      </div>
    </div>
  );
}
